import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ImageFromFileName } from "../ImageFromFileName";
import { MdaiLink } from "../MdaiLink";

const truncate = (text: string, max: number) => {
  if (!text) return text;
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
};

export interface TileProps {
  id?: string | number;
  image: string;
  imageAlt?: string;
  imageStyle?: React.CSSProperties;
  title: string;
  source?: string;
  author?: string;
  location?: string;
  avatar?: string;
  avatarAlt?: string;
  date: string;
  description: string;
  showDescription?: boolean;
  link: string;
  linkText?: string;
  isHero?: boolean;
  loading?: boolean;
}

export function Tile({
  image,
  imageAlt,
  imageStyle,
  title,
  source,
  author,
  location,
  date,
  description,
  showDescription = false,
  link,
  linkText = "Read More",
  isHero,
  avatar,
  avatarAlt,
  loading,
}: TileProps) {
  return (
    <Card
      sx={{
        minHeight: 240,
        flex: 1,
        width: "100%",
        height: "100%",
        padding: 3,
        display: "flex",
        flexDirection: "column",
        backgroundColor: "transparent",
        borderRadius: 2,
        border: "2px solid #444444",
        transition: "border-color 150ms ease",
        "&:hover": {
          borderColor: "var(--mdai-branded-purple)",
          backgroundColor: "transparent",
          "& .tile-link": {
            color: "secondary.dark",
            transition: "color 150ms ease",
          },
        },
      }}
    >
      <MdaiLink
        disabled={loading}
        href={link}
        aria={`Read more about ${title}`}
        style={{
          textDecoration: "none",
        }}
        children={
          <CardContent
            sx={{
              borderRadius: 2,
              color: "text.primary",
              p: "0 !important",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
            }}
          >
            <Stack
              flexDirection={isHero ? { sm: "column", lg: "row" } : "column"}
              gap={2}
              sx={{ flex: 1, minHeight: 0 }}
              alignItems={
                isHero ? { sm: "center", md: "stretch" } : "flex-start"
              }
              alignContent={"stretch"}
            >
              <Box
                sx={{
                  display: "flex",
                  alignSelf: "stretch",
                  height: isHero ? { sm: "126px", lg: "210px" } : "206px",
                  width: isHero ? { sm: "100%", lg: 460 } : "100%",
                  flexDirection: "column",
                  gap: "10px",
                  borderRadius: 2,
                }}
              >
                <ImageFromFileName
                  fileName={image}
                  alt={imageAlt}
                  sx={{
                    height: "100%",
                    width: "100%",
                    objectFit: "cover",
                    borderRadius: 1,
                    ...imageStyle,
                  }}
                />
              </Box>
              <Stack
                sx={{ gap: 2, flex: 1, minHeight: 0 }}
                justifyContent={"space-between"}
                direction={"column"}
                alignSelf={"stretch"}
              >
                <Typography variant={isHero ? "h4" : "h5"}>
                  {truncate(title, 100)}
                </Typography>
                {(author ?? source) && (
                  <Stack
                    sx={{
                      justifyContent: "space-between",
                      alignItems: "center",
                      alignSelf: "stretch",
                    }}
                    direction={"row"}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      {avatar && (
                        <ImageFromFileName
                          fileName={avatar}
                          alt={avatarAlt}
                          sx={{
                            height: "24px",
                            width: "24px",
                            aspectRatio: "1/1",
                            borderRadius: "40px",
                          }}
                        />
                      )}

                      <Typography variant="body1">
                        {source ?? author}
                      </Typography>
                    </div>
                    {date && <Typography variant="body1">{date}</Typography>}
                  </Stack>
                )}
                {location && (
                  <Stack>
                    <Typography variant="body1">{location}</Typography>
                    {date && <Typography variant="body1">{date}</Typography>}
                  </Stack>
                )}
                {!(author ?? source ?? location) && (
                  <Stack>
                    {date && <Typography variant="body1">{date}</Typography>}
                  </Stack>
                )}
                {(!!isHero || showDescription) && (
                  <Typography variant="body1">
                    {truncate(description, 160)}
                  </Typography>
                )}
                <Typography
                  variant="body1"
                  display={"flex"}
                  className="tile-link"
                >
                  {linkText}
                  {link.startsWith("https://") && <ArrowOutwardRoundedIcon />}
                </Typography>
              </Stack>
            </Stack>
          </CardContent>
        }
      />
    </Card>
  );
}
